import type { AppTranslateFn } from "@/hooks/use-app-translation";
import type {
	RecordType,
	RecordTypeDisplay,
} from "@/services/record/record.types";
import {
	type ConfigurationTypeTableAccessDisplay,
	type ConfigurationTypeTableAccessKeys,
	getConfigurationTypeTableAccessDisplay,
} from "@/utils/configuration-type-table-access/configuration-type-table-access.utils";

export type RecordTypeScope = "platform" | "organization";

type RecordTypeSystemFields = Pick<
	RecordType | RecordTypeDisplay,
	"defaultType"
>;

export function isSystemRecordType(row: RecordTypeSystemFields): boolean {
	if (typeof row.defaultType === "boolean") return row.defaultType;
	return false;
}

export function isCustomRecordType(row: RecordTypeSystemFields): boolean {
	if (typeof row.defaultType === "boolean") return !row.defaultType;
	return false;
}

/**
 * Whether the delete action should be shown in the given scope.
 * Platform scope: all types are system, so always show delete.
 * Organization scope: only show delete for custom types.
 */
export function canShowDeleteRecordType(
	scope: RecordTypeScope,
	row: RecordType,
): boolean {
	if (scope === "platform") return isSystemRecordType(row);
	return isCustomRecordType(row);
}

export function getRecordTypeDisplayName(row: RecordType): string {
	return row.name?.trim() || "";
}

/** Access tab is only shown in org admin context. */
export function canShowAccessTab(scope: RecordTypeScope): boolean {
	return scope === "organization";
}

/** Activity tab is shown for every type in platform and org admin. */
export function canShowActivityTab(
	scope: RecordTypeScope,
	_row: RecordType,
): boolean {
	if (scope === "platform" || scope === "organization") return true;
	return false;
}

/** Org-wide disabled when `enabled === false`. */
export function isRecordTypeOrgAccessDisabled(
	row: Pick<RecordType, "enabled">,
): boolean {
	return row.enabled === false;
}

/**
 * Org-admin configuration table: all types remain visible when org-disabled
 * so Access tab changes are reflected in the table (e.g. "Not available").
 */
export function shouldShowOrgAdminRecordTypesTableRow(
	_row: Pick<RecordType, "defaultType" | "enabled">,
): boolean {
	return true;
}

export type RecordTypeTableAccessDisplay = ConfigurationTypeTableAccessDisplay;

const RECORD_TYPE_TABLE_ACCESS_KEYS = {
	available: "admin.recordTypes.table.access.available",
	notAvailable: "admin.recordTypes.table.access.notAvailable",
	noSubOrganizations: "org-admin.recordTypes.table.access.noSubOrganizations",
	allSubOrganizations: "org-admin.recordTypes.table.access.allSubOrganizations",
	partial: "admin.recordTypes.table.access.partial",
	tooltipNone: "admin.recordTypes.table.access.tooltipNone",
} satisfies ConfigurationTypeTableAccessKeys;

/** Org-admin Record Types table/export Access column label and badge color. */
export function getRecordTypeTableAccessDisplay(
	row: RecordType,
	totalSubOrganizations: number,
	t: AppTranslateFn,
): RecordTypeTableAccessDisplay {
	return getConfigurationTypeTableAccessDisplay({
		enabled: row.enabled,
		grantedCount: row.recordTypeSubOrganizations?.length ?? 0,
		totalSubOrganizations,
		keys: RECORD_TYPE_TABLE_ACCESS_KEYS,
		t,
	});
}
